import { useForm } from "@tanstack/react-form";
import { Button, Stack } from "@mantine/core";
import { useLocation } from "react-router-dom";
import {
  ItemTypeEnum,
  DeepPartial,
  materialCharacteristicsSchema,
  type MaterialCharacteristics,
  areMaterialCharacteristicsEqual,
} from "@jasper/client-models";
import { useUpsertItem } from "@jasper/use-cases";
import {
  ProductMaterialCharacteristicsForm,
  validateSchema,
} from "@jasper/forms";

import { STYLE } from "../../types/constants";
import { deduceShadeType } from "../../utils/item.utils";
import { ItemType, TeethShadeType, TeethShadeSide } from "../../types/enums";
import { notifications } from "@mantine/notifications";

type ItemMaterialCharacteristics = {
  tooth: number;
  materialCharacteristics: MaterialCharacteristics;
};

type ProductMaterialCharacteristics = {
  items: ItemMaterialCharacteristics[];
};

const computeDefaultMaterialCharacteristics = ({
  accountProductPreferences,
  item,
  order,
}: {
  accountProductPreferences: any;
  item: any;
  order: any;
}) => {
  return {
    material: item?.itemMaterial?.id,
    shadeCharacteristics:
      item?.itemShade || item?.multiShadeInfo?.length
        ? {
            shade: item?.itemShade?.id,
            shadeType: deduceShadeType(
              item?.itemShade?.id,
              order?.user?.id,
              accountProductPreferences,
              item?.teethshadeType === TeethShadeType.MULTI_SHADE,
              item?.multiShadeInfo ?? []
            ),
            isMultiShade: item?.teethshadeType === TeethShadeType.MULTI_SHADE,
            gingivalShade:
              (item?.multiShadeInfo ?? []).find(
                (multiShadeInfo: { teethShadeSide: TeethShadeSide }) =>
                  multiShadeInfo.teethShadeSide === TeethShadeSide.GINGIVAL
              )?.itemShade?.id ?? undefined,
            baseShade:
              (item?.multiShadeInfo ?? []).find(
                (multiShadeInfo: { teethShadeSide: TeethShadeSide }) =>
                  multiShadeInfo.teethShadeSide === TeethShadeSide.BASE
              )?.itemShade?.id ?? undefined,
            incisalShade:
              (item?.multiShadeInfo ?? []).find(
                (multiShadeInfo: { teethShadeSide: TeethShadeSide }) =>
                  multiShadeInfo.teethShadeSide === TeethShadeSide.INCISAL
              )?.itemShade?.id ?? undefined,
          }
        : undefined,
  };
};

const CreateNewBridge = ({
  accountProductPreferences,
  order,
  onSubmit,
  productId,
}) => {
  const location = useLocation();

  const savedProduct = location?.state?.product;
  const savedItems = (savedProduct?.anatomyItem ?? []).filter(
    item => item.itemType === ItemType.BRIDGE
  );

  const {
    isLoading: isCreateItemLoading,
    itemMaterials,
    itemShadeTypes,
    itemShades,
    upsertItems,
    isUpserting,
    isDeleting,
    deleteItems,
  } = useUpsertItem({
    item: {
      type: ItemTypeEnum.bridge,
    },
    productId: productId ?? savedProduct?.id,
  });

  const defaultValues: {
    productMaterialCharacteristics: DeepPartial<ProductMaterialCharacteristics>;
  } = {
    productMaterialCharacteristics: {
      items: (savedProduct?.teeth ?? []).map(tooth => {
        const savedItemForTooth =
          savedItems.find(item => item.teeth.includes(tooth)) ?? savedItems[0];

        return {
          tooth,
          materialCharacteristics: computeDefaultMaterialCharacteristics({
            accountProductPreferences,
            item: savedItemForTooth,
            order,
          }),
        };
      }),
    },
  };

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const items = value.productMaterialCharacteristics?.items ?? [];

      try {
        if (
          savedItems &&
          savedItems.filter(item => item.itemType === ItemType.BRIDGE).length >
            items.length
        ) {
          const itemsToDelete = savedItems
            .filter(item => item.itemType === ItemType.BRIDGE)
            .slice(items.length);

          await deleteItems(itemsToDelete.map(item => item.id));
        }

        await upsertItems(
          (() => {
            const consumedSavedItemIds = new Set<string>();

            return items.map(item => {
              const matchingSavedItem = savedItems.find(savedItem => {
                if (
                  !savedItem.teeth.includes(Number(item.tooth)) ||
                  consumedSavedItemIds.has(savedItem.id)
                ) {
                  return false;
                }

                return true;
              });

              if (matchingSavedItem?.id) {
                consumedSavedItemIds.add(matchingSavedItem.id);
              }

              return {
                materialCharacteristics:
                  item.materialCharacteristics as MaterialCharacteristics,
                teeth: [Number(item.tooth)],
                itemId: matchingSavedItem?.id,
              };
            });
          })()
        );
      } catch (e) {
        notifications.show({
          title: "Bridge creation failed",
          message:
            "An error occurred while saving the bridge. Please try again.",
          color: "red",
        });
      }

      onSubmit();
    },
  });

  if (isCreateItemLoading) return null;

  return (
    <Stack gap="md">
      <form.Field
        name="productMaterialCharacteristics"
        validators={{
          onSubmit: ({ value }) => {
            const { items = [] } = value ?? {};

            const itemErrors = items.map(item => {
              const result = validateSchema(
                materialCharacteristicsSchema,
                item.materialCharacteristics
              );

              if (result.success === true) {
                return undefined;
              }

              return {
                materialCharacteristics: result.errors,
              };
            });

            if (!itemErrors?.some(Boolean)) {
              return;
            }

            return {
              items: itemErrors,
            };
          },
        }}
      >
        {field => {
          const onSubmitErrors = field.state.meta.errorMap.onSubmit;
          const productMaterialCharacteristics = field.state.value;

          // Bridge is per tooth if items have different material or shade characteristics
          const isPerTooth =
            productMaterialCharacteristics?.items?.some(
              item =>
                !areMaterialCharacteristicsEqual(
                  item.materialCharacteristics,
                  productMaterialCharacteristics.items[0]
                    .materialCharacteristics
                )
            ) ?? false;

          return (
            <ProductMaterialCharacteristicsForm
              productType={ItemTypeEnum.bridge}
              isSolidarized
              defaultIsPerTooth={isPerTooth}
              itemMaterials={itemMaterials}
              itemShadeTypes={itemShadeTypes}
              itemShades={itemShades}
              teeth={savedProduct?.teeth ?? []}
              value={productMaterialCharacteristics}
              errors={
                typeof onSubmitErrors === "object" ? onSubmitErrors : undefined
              }
              onChange={next => field.setValue(next)}
              validateTooth={tooth => {
                const toothCharacteristics =
                  productMaterialCharacteristics?.items?.find(
                    item => item.tooth === tooth
                  )?.materialCharacteristics;
                const res = validateSchema(
                  materialCharacteristicsSchema,
                  toothCharacteristics
                );

                if (res.success === true) {
                  return;
                }

                return res.errors;
              }}
            />
          );
        }}
      </form.Field>
      <div style={{ textAlign: "center" }}>
        <Button
          loading={isUpserting || isDeleting}
          style={{ backgroundColor: STYLE.primary }}
          onClick={form.handleSubmit}
        >
          Save
        </Button>
      </div>
    </Stack>
  );
};

export default CreateNewBridge;
